import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import SegmentedControl from '@/components/ui/SegmentedControl';
import type { SimulationPayload } from '@/api/engineService';
import { sportColor } from '@/lib/sportColors';
import { useChartTheme } from '@/features/dashboard/blocks/chartTheme';
import { PLAN_TYPE_LABELS } from '@/features/plans/planAllocation';
import { DAYS, formatFr, formatHours, PREPARATION_COLORS, PREPARATION_LABELS, SPORT_SHORT } from './simulatorUtils';

/** « Ta semaine type »: the days the athlete opened, per sport, plus the brick day. */
const WeekTemplateStrip = ({ settings }: { settings: NonNullable<SimulationPayload['recap']['settings']> }) => (
  <div className="grid grid-cols-7 gap-1">
    {DAYS.map((d) => {
      const sportIds = [...new Set(settings.sportDays.filter((s) => s.dow === d.value).map((s) => s.sportId))].sort();
      return (
        <div key={d.value} className="flex min-h-[3.5rem] flex-col items-center gap-1 rounded-md border border-gray-200 p-1 text-[11px] dark:border-gray-800">
          <span className="font-medium text-gray-500">{d.label.slice(0, 3)}</span>
          {sportIds.map((s) => (
            <span key={s} className="rounded px-1.5 py-0.5 text-gray-800" style={{ backgroundColor: sportColor(s) }}>
              {SPORT_SHORT[s] ?? s}
            </span>
          ))}
          {settings.brickDow === d.value && <span className="rounded bg-success-500/20 px-1.5 py-0.5 text-success-600">Enchaînement</span>}
        </div>
      );
    })}
  </div>
);

const WeeklyVolumeChart = ({ volume, sportIds }: { volume: SimulationPayload['recap']['weeklyVolume']; sportIds: string[] }) => {
  const t = useChartTheme();
  const [sport, setSport] = useState('all');
  const series = sport === 'all' ? sportIds : [sport];
  const data = volume.map((v, i) => {
    const row: Record<string, number | string> = { week: i + 1, label: `S${i + 1}`, phase: v.phase };
    for (const s of series) row[s] = Math.round(((v.bySport[s] ?? 0) / 3600) * 100) / 100;
    return row;
  });
  return (
    <div className="flex flex-col gap-2">
      <SegmentedControl
        size="sm"
        value={sport}
        onChange={setSport}
        options={[{ value: 'all', label: 'Tous sports' }, ...sportIds.map((s) => ({ value: s, label: SPORT_SHORT[s] ?? s }))]}
      />
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid stroke={t.grid} vertical={false} />
            <XAxis dataKey="label" stroke={t.axis} tick={{ fontSize: 11 }} />
            <YAxis stroke={t.axis} tick={{ fontSize: 11 }} unit="h" />
            <Tooltip
              contentStyle={{ backgroundColor: t.surface, borderColor: t.border, fontSize: 12 }}
              formatter={(value, name) => [`${value} h`, SPORT_SHORT[String(name)] ?? name]}
              labelFormatter={(label, payload) => `${label}${payload?.[0]?.payload?.phase === 'BACKUP' ? ' · Attente' : ''}`}
            />
            <Legend formatter={(value) => SPORT_SHORT[value] ?? value} />
            {series.map((s) => (
              <Bar key={s} dataKey={s} stackId="v" fill={sportColor(s)} shape={(props: unknown) => <BackupAwareBar {...(props as BarShapeProps)} />} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: { phase?: string };
}

const BackupAwareBar = (props: BarShapeProps) => {
  const { x = 0, y = 0, width = 0, height = 0, fill, payload } = props;
  if (height <= 0) return null;
  return <rect x={x} y={y} width={width} height={height} fill={fill} opacity={payload?.phase === 'BACKUP' ? 0.45 : 1} />;
};

const RecapCard = ({
  sim,
  planName,
  formatCode,
  planType,
  competitionDate,
  onShowDropped,
}: {
  sim: SimulationPayload;
  planName: string;
  formatCode: string;
  planType: string;
  competitionDate?: string;
  onShowDropped: () => void;
}) => {
  const prep = sim.messages.preparation;
  const sportIds = [...new Set(sim.recap.weeklyVolume.flatMap((v) => Object.keys(v.bySport)))].sort();
  return (
    <Card className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{planName}</p>
        <Badge color="blue">{PLAN_TYPE_LABELS[planType] ?? planType}</Badge>
        <Badge color="blue">{formatCode}</Badge>
        {competitionDate && <span className="text-sm text-gray-500">Compétition le {formatFr(competitionDate)}</span>}
        {sim.preparation && <Badge color={PREPARATION_COLORS[sim.preparation]}>Préparation {PREPARATION_LABELS[sim.preparation].toLowerCase()}</Badge>}
      </div>

      {prep.title && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          <p className="font-semibold">{prep.title}</p>
          <p className="mt-1">{prep.body}</p>
          {prep.reductions.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {prep.reductions.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {prep.closing && <p className="mt-1">{prep.closing}</p>}
          {prep.testsRemoved && <p className="mt-1">{prep.testsRemoved}</p>}
        </div>
      )}
      {!prep.title && prep.testsRemoved && <p className="text-sm text-amber-700">{prep.testsRemoved}</p>}

      <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
        <div className="flex flex-col gap-1 text-gray-700 dark:text-gray-200">
          {sim.backup && (
            <p>
              Plan d'attente : <span className="font-medium">{sim.backup.planName}</span> du {formatFr(sim.backup.from)} au {formatFr(sim.backup.to)}
            </p>
          )}
          <p>
            Début de la préparation : <span className="font-medium">{formatFr(sim.planStartDate)}</span>
          </p>
          {sim.recovery && (
            <p>
              Récupération : <span className="font-medium">{sim.recovery.weeks} semaine(s)</span> du{' '}
              {formatFr(sim.recovery.from)} au {formatFr(sim.recovery.to)}
            </p>
          )}
          <p>
            Durée : <span className="font-medium">{sim.recap.actualWeeks} semaines</span> · idéal {sim.recap.idealWeeks}
          </p>
          <p>
            {/* R43: the range covers the weeks that hold sessions; rest weeks are stated apart. */}
            Volume : <span className="font-medium">{sim.recap.sessionsPerWeek.min} à {sim.recap.sessionsPerWeek.max} séances / semaine</span>
            {sim.recap.restWeeks > 0 && <> · <span className="font-medium">{sim.recap.restWeeks} semaine(s) de repos</span></>}
          </p>
          {sim.recap.settings && (
            <p>
              Réglages :{' '}
              <span className="font-medium">
                {Object.entries(sim.recap.settings.sessionsPerWeek)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([sportId, n]) => `${n} ${SPORT_SHORT[sportId] ?? sportId}`)
                  .join(' · ')}
              </span>
              {' · '}PPG {sim.recap.settings.includePpg ? 'activée' : 'désactivée'}
            </p>
          )}
        </div>
        {sim.recap.settings && (
          <div>
            <p className="mb-1 text-xs font-medium uppercase text-gray-500">Semaine type</p>
            <WeekTemplateStrip settings={sim.recap.settings} />
          </div>
        )}
      </div>

      <div>
        <p className="mb-1 text-xs font-medium uppercase text-gray-500">Heures par sport et par semaine</p>
        <WeeklyVolumeChart volume={sim.recap.weeklyVolume} sportIds={sportIds} />
        <p className="mt-1 text-xs text-gray-400">
          Total {formatHours(sim.recap.weeklyVolume.reduce((s, v) => s + Object.values(v.bySport).reduce((a, b) => a + b, 0), 0))} · les semaines d'attente sont en
          transparence.
        </p>
      </div>

      {sim.warnings.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium uppercase text-gray-500">Alertes</p>
          <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
            {sim.warnings.map((w, i) => (
              <li key={i} className="flex items-center gap-2">
                <Badge color={w.phase === 'BACKUP' ? 'gray' : 'yellow'}>{w.phase === 'BACKUP' ? 'Attente' : 'Plan'}</Badge>
                {w.message}
                {((w.code === 'PARTIAL_FIRST_WEEK' && Number(w.detail?.dropped ?? 0) > 0) ||
                  w.code === 'TESTS_DROPPED_SAME_DAY' ||
                  w.code === 'SESSIONS_DROPPED_FOR_TEST') && (
                  <button type="button" className="text-xs text-brand-500 hover:underline" onClick={onShowDropped}>
                    Afficher les séances écartées
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default RecapCard;
